import React, { useEffect, useMemo, useState } from "react";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, User as UserIcon,
  Users, Trash2, AlertTriangle, Flame, CircleAlert, X, Check, Edit3, Sparkles,
  MessageCircle,
} from "lucide-react";
import { endpoints } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { openWhatsApp } from "../lib/wa";
import { toast } from "sonner";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function isoDate(d) { return d.toISOString().slice(0, 10); }
function startOfWeek(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}

export default function CalendarPage() {
  const [view, setView] = useState("upcoming");
  const [anchor, setAnchor] = useState(() => new Date());  // the reference date
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [members, setMembers] = useState([]);

  // Dialogs
  const [trainersOpen, setTrainersOpen] = useState(false);
  const [classOpen, setClassOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [eventDefaultDate, setEventDefaultDate] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [bookContext, setBookContext] = useState(null); // {class_id, name, instance_date, booked, capacity}

  const loadAll = async () => {
    setLoading(true);
    try {
      let calStartDate;
      let calView;
      if (view === "week") {
        calStartDate = isoDate(startOfWeek(anchor));
        calView = "week";
      } else if (view === "month") {
        // First day of month
        const m = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        calStartDate = isoDate(m);
        calView = "month";
      } else if (view === "year") {
        // Year overview - pull all events for the year
        calStartDate = isoDate(new Date(anchor.getFullYear(), 0, 1));
        calView = "month";
      } else if (view === "events") {
        calStartDate = isoDate(anchor);
        calView = "week";
      } else {
        calStartDate = isoDate(anchor);
        calView = "day";
      }
      const [cal, ts, cs, ev, ms] = await Promise.all([
        endpoints.calendar(calStartDate, calView),
        endpoints.listTrainers(),
        endpoints.listClasses(),
        endpoints.listEvents(false),
        endpoints.listMembers(),
      ]);
      setData(cal.data);
      setTrainers(ts.data);
      setClasses(cs.data);
      setEvents(ev.data);
      setMembers(ms.data);
    } catch (e) {
      toast.error("Could not load calendar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [view, anchor]);

  const stepAnchor = (delta) => {
    const x = new Date(anchor);
    if (view === "day") x.setDate(x.getDate() + delta);
    else if (view === "month") x.setMonth(x.getMonth() + delta);
    else if (view === "year") x.setFullYear(x.getFullYear() + delta);
    else x.setDate(x.getDate() + delta * 7);
    setAnchor(x);
  };
  const resetToday = () => setAnchor(new Date());

  const label = useMemo(() => {
    if (view === "day") return anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    if (view === "month") return anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    if (view === "year") return String(anchor.getFullYear());
    if (view === "week") {
      const s = startOfWeek(anchor);
      const e = new Date(s); e.setDate(e.getDate() + 6);
      return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return "All upcoming events";
  }, [anchor, view]);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground/70 mb-2">Operations</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground" data-testid="calendar-title">
            Calendar
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Classes, trainers and events - today's operational screen.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="manage-trainers-btn"
            onClick={() => setTrainersOpen(true)}
            className="inline-flex items-center gap-1.5 bg-card border border-border hover:bg-muted/50 text-foreground text-sm font-medium px-3 py-2 rounded-lg transition-colors"
          >
            <UserIcon className="w-4 h-4" /> Trainers
          </button>
          <button
            data-testid="add-event-btn"
            onClick={() => setEventOpen(true)}
            className="inline-flex items-center gap-1.5 bg-card border border-border hover:bg-muted/50 text-foreground text-sm font-medium px-3 py-2 rounded-lg transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Add event
          </button>
          <button
            data-testid="add-class-btn"
            onClick={() => setClassOpen(true)}
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-medium px-4 py-2.5 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> Add class
          </button>
        </div>
      </header>

      {/* View tabs + nav */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-muted/30 p-1 rounded-lg" data-testid="calendar-tabs">
          {["upcoming", "day", "week", "month", "year", "events"].map(k => (
            <button
              key={k}
              onClick={() => setView(k)}
              data-testid={`calendar-view-${k}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize transition-colors ${
                view === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
        {view !== "events" && view !== "upcoming" && (
          <div className="flex items-center gap-2">
            <button onClick={() => stepAnchor(-1)} data-testid="cal-prev" className="p-1.5 rounded-md border border-border hover:bg-muted/50 text-foreground/80" aria-label="Previous">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={resetToday} data-testid="cal-today" className="text-xs font-medium px-3 py-1.5 border border-border rounded-md hover:bg-muted/50 text-foreground/80">Today</button>
            <button onClick={() => stepAnchor(1)} data-testid="cal-next" className="p-1.5 rounded-md border border-border hover:bg-muted/50 text-foreground/80" aria-label="Next">
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium text-foreground ml-2" data-testid="cal-label">{label}</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="border border-border bg-card rounded-xl p-10 text-center text-sm text-muted-foreground" data-testid="calendar-loading">Loading schedule…</div>
      ) : view === "day" ? (
        <DayView
          data={data}
          onBook={(inst) => { setBookContext(inst); setBookOpen(true); }}
          onDeleteClass={async (cid) => {
            if (!window.confirm("Delete this recurring class?")) return;
            await endpoints.deleteClass(cid);
            toast.success("Class removed");
            loadAll();
          }}
        />
      ) : view === "week" ? (
        <WeekView
          data={data}
          onBook={(inst) => { setBookContext(inst); setBookOpen(true); }}
        />
      ) : view === "month" ? (
        <MonthView
          anchor={anchor}
          data={data}
          events={events}
          onPickDay={(d) => { setAnchor(d); setView("day"); }}
          onAddEvent={(dateStr) => { setEventDefaultDate(dateStr); setEventOpen(true); }}
          onEventMoved={loadAll}
        />
      ) : view === "year" ? (
        <YearView
          anchor={anchor}
          data={data}
          events={events}
          onPickMonth={(m) => { setAnchor(m); setView("month"); }}
        />
      ) : view === "upcoming" ? (
        <UpcomingView
          data={data}
          events={events}
          onBook={(inst) => { setBookContext(inst); setBookOpen(true); }}
        />
      ) : (
        <EventsView events={events} onRefresh={loadAll} />
      )}

      {/* Dialogs */}
      <TrainersDialog
        open={trainersOpen}
        onOpenChange={setTrainersOpen}
        trainers={trainers}
        onChanged={loadAll}
      />
      <ClassDialog
        open={classOpen}
        onOpenChange={setClassOpen}
        trainers={trainers}
        onCreated={loadAll}
      />
      <EventDialog
        open={eventOpen}
        onOpenChange={(v) => { setEventOpen(v); if (!v) setEventDefaultDate(null); }}
        trainers={trainers}
        onCreated={loadAll}
        defaultDate={eventDefaultDate}
      />
      <BookDialog
        open={bookOpen}
        onOpenChange={setBookOpen}
        context={bookContext}
        members={members}
        onBooked={loadAll}
      />
    </div>
  );
}

// ---------- Day View ----------
function DayView({ data, onBook, onDeleteClass }) {
  const instances = (data?.instances || []).filter(i => i.instance_date === data.date_range[0]);
  instances.sort((a, b) => a.start_time.localeCompare(b.start_time));
  const events = (data?.events || []).filter(e => e.start_at.slice(0, 10) === data.date_range[0]);

  // Hourly slots 6am-23pm
  const hours = [];
  for (let h = 6; h <= 22; h++) hours.push(h);

  const instanceByHour = {};
  instances.forEach(i => {
    const h = parseInt(i.start_time.split(":")[0], 10);
    (instanceByHour[h] = instanceByHour[h] || []).push(i);
  });
  const eventByHour = {};
  events.forEach(e => {
    const h = new Date(e.start_at).getHours();
    (eventByHour[h] = eventByHour[h] || []).push(e);
  });

  const totalSlots = hours.length;
  const filledSlots = hours.filter(h => (instanceByHour[h]?.length || 0) + (eventByHour[h]?.length || 0) > 0).length;
  const emptySlots = totalSlots - filledSlots;

  return (
    <div>
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="day-kpis">
        <Kpi label="Classes today" value={instances.length} tone="text-emerald-600" />
        <Kpi label="Events today" value={events.length} tone="text-indigo-500" />
        <Kpi label="Total bookings" value={instances.reduce((s, i) => s + i.booked, 0)} tone="text-foreground" />
        <Kpi label="Empty slots" value={emptySlots} tone="text-rose-500" sub="Lost revenue" />
      </div>

      <div className="border border-border bg-card rounded-xl overflow-hidden" data-testid="day-timeline">
        {hours.map(h => {
          const list = instanceByHour[h] || [];
          const evList = eventByHour[h] || [];
          const isEmpty = list.length + evList.length === 0;
          return (
            <div key={h} className={`flex items-stretch border-b border-border/60 last:border-0 ${isEmpty ? "bg-rose-50/30 dark:bg-rose-950/10" : ""}`}>
              <div className="w-20 shrink-0 p-3 text-xs font-semibold text-muted-foreground border-r border-border/60 tabular-nums flex items-start">
                {`${h.toString().padStart(2, "0")}:00`}
              </div>
              <div className="flex-1 p-2 min-h-[64px] flex flex-wrap gap-2">
                {list.map(i => (
                  <ClassChip key={i.id} inst={i} onBook={() => onBook(i)} onDelete={() => onDeleteClass(i.class_id)} />
                ))}
                {evList.map(e => <EventChip key={e.id} ev={e} />)}
                {isEmpty && (
                  <span className="text-xs text-rose-500/70 italic self-center">Empty slot</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Kpi({ label, value, tone = "text-foreground", sub }) {
  return (
    <div className="border border-border rounded-xl p-4 bg-card">
      <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-1">{label}</p>
      <p className={`font-display text-2xl font-bold ${tone}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground/70 mt-0.5">{sub}</p>}
    </div>
  );
}

// ---------- Week View ----------
function WeekView({ data, onBook }) {
  const dates = data?.date_range || [];
  const instancesByDate = {};
  (data?.instances || []).forEach(i => {
    (instancesByDate[i.instance_date] = instancesByDate[i.instance_date] || []).push(i);
  });
  const eventsByDate = {};
  (data?.events || []).forEach(e => {
    const d = e.start_at.slice(0, 10);
    (eventsByDate[d] = eventsByDate[d] || []).push(e);
  });

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="week-view">
      {dates.map((date, i) => {
        const list = (instancesByDate[date] || []).sort((a, b) => a.start_time.localeCompare(b.start_time));
        const evs = eventsByDate[date] || [];
        const isToday = date === todayIso;
        const d = new Date(date);
        return (
          <div
            key={date}
            className={`border rounded-xl p-3 bg-card min-h-[220px] ${isToday ? "border-primary/60" : "border-border"}`}
            data-testid={`week-col-${date}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className={`text-[11px] font-semibold uppercase ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                  {DAY_NAMES[i]}
                </div>
                <div className={`font-display text-xl font-bold ${isToday ? "text-foreground" : "text-foreground/85"}`}>
                  {d.getDate()}
                </div>
              </div>
              {list.length === 0 && evs.length === 0 && (
                <span className="text-[10px] text-rose-500/80 italic">empty</span>
              )}
            </div>
            <div className="space-y-2">
              {list.map(inst => <ClassChip key={inst.id} inst={inst} compact onBook={() => onBook(inst)} />)}
              {evs.map(ev => <EventChip key={ev.id} ev={ev} compact />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Upcoming (simple cards - Phase B) ----------
function UpcomingView({ data, events, onBook }) {
  // Build a list of class instances from `data.instances` (the actual array
  // from /api/calendar response). `data.days` is an integer count, not an
  // array. We build display-friendly cards from instances + filter to future.
  const now = new Date();
  const dateRange = Array.isArray(data?.date_range) ? data.date_range.slice(0, 14) : [];
  const instances = Array.isArray(data?.instances) ? data.instances : [];

  const classCards = instances
    .filter(ci => dateRange.length === 0 || dateRange.includes(ci.instance_date))
    .map(ci => {
      const startAt = new Date(`${ci.instance_date}T${ci.start_time || "00:00"}:00`);
      const dt = new Date(`${ci.instance_date}T00:00:00`);
      return {
        ...ci,
        weekday: dt.toLocaleDateString(undefined, { weekday: "short" }),
        date_label: dt.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        start_at: startAt,
      };
    })
    .filter(ci => ci.start_at >= now)
    .sort((a, b) => a.start_at - b.start_at);

  const upcomingEvents = (Array.isArray(events) ? events : [])
    .filter(e => new Date(e.start_at) >= now)
    .slice(0, 8);

  const empty = classCards.length === 0 && upcomingEvents.length === 0;

  return (
    <div className="space-y-6" data-testid="upcoming-view">
      {empty && (
        <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-muted-foreground">
          Nothing scheduled in the next 14 days. Add a class or event above.
        </div>
      )}

      {classCards.length > 0 && (
        <section data-testid="upcoming-classes">
          <h2 className="font-display text-base font-semibold text-foreground mb-3 flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-muted-foreground" /> Upcoming classes
            <span className="text-xs text-muted-foreground/70 font-normal">({classCards.length})</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {classCards.slice(0, 12).map((c, i) => (
              <button
                key={`${c.id || c.class_id}-${c.instance_date}`}
                onClick={() => onBook?.(c)}
                data-testid={`upcoming-class-${i}`}
                className="text-left rounded-lg border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all p-4 group"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color || "#10B981" }} />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {c.weekday} · {c.date_label} · {c.start_time}
                  </p>
                </div>
                <p className="font-display text-base font-bold text-foreground truncate">{c.name}</p>
                {c.trainer_name && (
                  <p className="text-xs text-muted-foreground mt-1">with {c.trainer_name}</p>
                )}
                {(c.capacity || c.booked) && (
                  <div className="mt-3 flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">{c.booked || 0}/{c.capacity || "-"} booked</span>
                    <span className="text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">Book →</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {upcomingEvents.length > 0 && (
        <section data-testid="upcoming-events-section">
          <h2 className="font-display text-base font-semibold text-foreground mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-500" /> Upcoming events
            <span className="text-xs text-muted-foreground/70 font-normal">({upcomingEvents.length})</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {upcomingEvents.map((ev) => {
              const dt = new Date(ev.start_at);
              return (
                <div
                  key={ev.id}
                  data-testid={`upcoming-event-${ev.id}`}
                  className="rounded-lg border border-border bg-card p-4"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: ev.color || "#6366F1" }} />
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                    </p>
                  </div>
                  <p className="font-display text-base font-bold text-foreground truncate">{ev.name}</p>
                  {ev.notes && <p className="text-xs text-muted-foreground/85 mt-1 line-clamp-2">{ev.notes}</p>}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}


// ---------- Events View ----------
function EventsView({ events, onRefresh }) {
  const now = new Date();
  const upcoming = events.filter(e => new Date(e.end_at) >= now);
  const past = events.filter(e => new Date(e.end_at) < now);

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await endpoints.deleteEvent(id);
      toast.success("Event removed");
      onRefresh();
    } catch {
      toast.error("Could not delete");
    }
  };

  const Section = ({ title, items, empty }) => (
    <section className="mb-8" data-testid={`events-${title.toLowerCase()}`}>
      <h3 className="font-display text-base font-semibold text-foreground mb-3 flex items-center gap-2">
        <CalendarIcon className="w-4 h-4 text-muted-foreground" />
        {title} <span className="text-xs text-muted-foreground/70 font-normal">({items.length})</span>
      </h3>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground/70 border border-dashed border-border rounded-xl p-6 text-center">{empty}</div>
      ) : (
        <div className="border border-border rounded-xl divide-y divide-border/60 bg-card overflow-hidden">
          {items.map(e => (
            <div key={e.id} className="p-4 flex items-center gap-4" data-testid={`event-row-${e.id}`}>
              <div className="w-1.5 self-stretch rounded-full" style={{ backgroundColor: e.color || "#6366F1" }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground truncate">{e.name}</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(e.start_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  {" - "}
                  {new Date(e.end_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                  {e.trainer_name ? ` · ${e.trainer_name}` : ""}
                  {e.capacity ? ` · cap ${e.capacity}` : ""}
                </div>
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{e.kind}</span>
              <button onClick={() => remove(e.id)} className="p-2 text-muted-foreground/70 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-md" aria-label="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div>
      <Section title="Upcoming" items={upcoming} empty="No upcoming events scheduled." />
      <Section title="Past" items={past} empty="No past events." />
    </div>
  );
}

// ---------- Chips ----------
function ClassChip({ inst, compact = false, onBook, onDelete }) {
  const pct = inst.capacity > 0 ? (inst.booked / inst.capacity) * 100 : 0;
  const signals = inst.signals || [];
  const sigBadges = signals.map(s => ({
    fully_booked: { icon: Check, label: "Full", tone: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200" },
    low_attendance: { icon: AlertTriangle, label: "Low", tone: "bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-200" },
    trainer_overload: { icon: Flame, label: "Overload", tone: "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200" },
  }[s])).filter(Boolean);

  return (
    <div
      className="relative rounded-lg border border-border bg-card overflow-hidden group"
      data-testid={`class-chip-${inst.id}`}
      style={{ minWidth: compact ? undefined : 240 }}
    >
      <div className="h-1" style={{ backgroundColor: inst.color || "#22C55E" }} />
      <div className="p-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-foreground truncate">{inst.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">
              {inst.start_time} · {inst.duration_min}m{inst.trainer_name ? ` · ${inst.trainer_name}` : ""}
            </div>
          </div>
          <span className="text-[11px] font-semibold text-foreground tabular-nums shrink-0">
            {inst.booked}/{inst.capacity}
          </span>
        </div>
        <div className="mt-2 w-full h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-emerald-500" : pct >= 60 ? "bg-primary" : "bg-primary/60"}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        {sigBadges.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1" data-testid={`signals-${inst.id}`}>
            {sigBadges.map(({ icon: Icon, label, tone }, idx) => (
              <span key={idx} className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${tone}`}>
                <Icon className="w-3 h-3" /> {label}
              </span>
            ))}
          </div>
        )}
        <div className="mt-2 flex gap-1">
          <button
            onClick={onBook}
            data-testid={`book-class-${inst.id}`}
            disabled={inst.booked >= inst.capacity}
            className="flex-1 text-[11px] font-medium bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground py-1 rounded-md transition-colors"
          >
            Book
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              aria-label="Delete class"
              className="p-1 rounded-md text-muted-foreground/70 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function EventChip({ ev, compact }) {
  return (
    <div
      className="rounded-lg border border-border bg-card overflow-hidden"
      data-testid={`event-chip-${ev.id}`}
      style={{ minWidth: compact ? undefined : 240 }}
    >
      <div className="h-1" style={{ backgroundColor: ev.color || "#6366F1" }} />
      <div className="p-2.5">
        <div className="text-xs font-semibold text-foreground truncate flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-indigo-500 shrink-0" /> {ev.name}
        </div>
        <div className="text-[11px] text-muted-foreground truncate">
          {new Date(ev.start_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - {new Date(ev.end_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          {ev.trainer_name ? ` · ${ev.trainer_name}` : ""}
        </div>
      </div>
    </div>
  );
}

// ---------- Dialogs ----------
function TrainersDialog({ open, onOpenChange, trainers, onChanged }) {
  const [form, setForm] = useState({ name: "", phone: "", specialties: "", color: "#22C55E" });

  const add = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name required"); return; }
    try {
      await endpoints.createTrainer({
        name: form.name,
        phone: form.phone,
        specialties: form.specialties.split(",").map(s => s.trim()).filter(Boolean),
        color: form.color,
      });
      toast.success("Trainer added");
      setForm({ name: "", phone: "", specialties: "", color: "#22C55E" });
      onChanged();
    } catch { toast.error("Could not add trainer"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this trainer? They will be unlinked from classes/events.")) return;
    try { await endpoints.deleteTrainer(id); toast.success("Removed"); onChanged(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Trainers</DialogTitle></DialogHeader>
        <form onSubmit={add} className="space-y-3 pb-3 border-b border-border">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input data-testid="trainer-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Phone (optional)</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <div><Label>Specialties (comma-separated)</Label><Input placeholder="MMA, Boxing" value={form.specialties} onChange={e => setForm({ ...form, specialties: e.target.value })} /></div>
            <div>
              <Label>Color</Label>
              <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-14 h-10 rounded border border-border bg-card" />
            </div>
          </div>
          <button type="submit" data-testid="add-trainer-submit" className="w-full bg-primary text-white text-sm font-medium py-2 rounded-lg hover:bg-primary/90 transition-colors">Add trainer</button>
        </form>
        <div className="max-h-[280px] overflow-auto space-y-1 mt-3">
          {trainers.length === 0 ? (
            <div className="text-sm text-muted-foreground/70 text-center py-4">No trainers yet.</div>
          ) : trainers.map(t => (
            <div key={t.id} className="flex items-center gap-3 p-2 hover:bg-muted/40 rounded-md" data-testid={`trainer-row-${t.id}`}>
              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: t.color || "#22C55E" }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">{t.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{(t.specialties || []).join(", ") || "-"}{t.phone ? ` · ${t.phone}` : ""}</div>
              </div>
              <button onClick={() => remove(t.id)} className="p-1.5 text-muted-foreground/70 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-md" aria-label="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ClassDialog({ open, onOpenChange, trainers, onCreated }) {
  const [form, setForm] = useState({
    name: "MMA", trainer_id: "",
    day_of_week: 0, start_time: "18:00", duration_min: 60, capacity: 20,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name required"); return; }
    try {
      await endpoints.createClass({
        name: form.name,
        trainer_id: form.trainer_id || null,
        day_of_week: parseInt(form.day_of_week, 10),
        start_time: form.start_time,
        duration_min: parseInt(form.duration_min, 10) || 60,
        capacity: parseInt(form.capacity, 10) || 1,
      });
      toast.success("Class added");
      onOpenChange(false);
      onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">New recurring class</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="class-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label>
              <Input data-testid="class-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="MMA / Boxing / Strength / Custom" />
            </div>
            <div>
              <Label>Trainer (optional)</Label>
              <Select value={form.trainer_id || "__none"} onValueChange={v => setForm({ ...form, trainer_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="class-trainer-select"><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  {trainers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label>Day</Label>
              <Select value={String(form.day_of_week)} onValueChange={v => setForm({ ...form, day_of_week: parseInt(v, 10) })}>
                <SelectTrigger data-testid="class-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAY_NAMES.map((d, idx) => <SelectItem key={d} value={String(idx)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Start</Label><Input type="time" data-testid="class-start-input" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} /></div>
            <div><Label>Duration (min)</Label><Input type="number" value={form.duration_min} onChange={e => setForm({ ...form, duration_min: e.target.value })} /></div>
            <div><Label>Capacity</Label><Input type="number" data-testid="class-capacity-input" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button type="submit" data-testid="class-submit" className="w-full bg-primary text-white font-medium py-2.5 rounded-lg hover:bg-primary/90 transition-colors">
              Create recurring class
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EventDialog({ open, onOpenChange, trainers, onCreated, defaultDate }) {
  const now = new Date();
  const defStart = defaultDate ? new Date(defaultDate + "T10:00") : new Date(now.getTime() + 60 * 60 * 1000);
  const defEnd = defaultDate ? new Date(defaultDate + "T11:00") : new Date(now.getTime() + 2 * 60 * 60 * 1000);
  const fmtDT = (d) => {
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
  };
  const fmtDate = (d) => {
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 10);
  };
  const [multiDay, setMultiDay] = useState(false);
  const [form, setForm] = useState({
    name: "", kind: "workshop",
    start_at: fmtDT(defStart), end_at: fmtDT(defEnd),
    start_date: fmtDate(defStart), end_date: fmtDate(defStart),
    capacity: "", trainer_id: "", color: "#6366F1",
  });

  // Reset form when dialog opens with a new defaultDate
  useEffect(() => {
    if (!open) return;
    const s = defaultDate ? new Date(defaultDate + "T10:00") : new Date(Date.now() + 60 * 60 * 1000);
    const e = defaultDate ? new Date(defaultDate + "T11:00") : new Date(Date.now() + 2 * 60 * 60 * 1000);
    setForm(f => ({
      ...f,
      start_at: fmtDT(s),
      end_at: fmtDT(e),
      start_date: fmtDate(s),
      end_date: fmtDate(s),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultDate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name required"); return; }
    let startISO, endISO;
    if (multiDay) {
      // All-day multi-day: 09:00 start of first day → 21:00 end of last day
      if (!form.start_date || !form.end_date) { toast.error("Pick start and end dates"); return; }
      if (form.end_date < form.start_date) { toast.error("End date must be after start"); return; }
      startISO = new Date(form.start_date + "T09:00").toISOString();
      endISO = new Date(form.end_date + "T21:00").toISOString();
    } else {
      startISO = new Date(form.start_at).toISOString();
      endISO = new Date(form.end_at).toISOString();
    }
    try {
      await endpoints.createEvent({
        name: form.name,
        kind: form.kind,
        start_at: startISO,
        end_at: endISO,
        capacity: form.capacity ? parseInt(form.capacity, 10) : null,
        trainer_id: form.trainer_id || null,
        color: form.color,
      });
      toast.success("Event added");
      onOpenChange(false);
      onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">New event</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="event-form">
          <div>
            <Label>Name</Label>
            <Input data-testid="event-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sparring competition, Open house weekend" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kind</Label>
              <Select value={form.kind} onValueChange={v => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="event-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["workshop", "competition", "special", "other"].map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-foreground select-none cursor-pointer" data-testid="event-multiday-toggle">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-primary"
                  checked={multiDay}
                  onChange={(e) => setMultiDay(e.target.checked)}
                />
                Multi-day event
              </label>
            </div>
          </div>
          {multiDay ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start date</Label>
                <Input type="date" data-testid="event-start-date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label>End date</Label>
                <Input type="date" data-testid="event-end-date" value={form.end_date} min={form.start_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input type="datetime-local" data-testid="event-start-input" value={form.start_at} onChange={e => setForm({ ...form, start_at: e.target.value })} /></div>
              <div><Label>End</Label><Input type="datetime-local" data-testid="event-end-input" value={form.end_at} onChange={e => setForm({ ...form, end_at: e.target.value })} /></div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Capacity (optional)</Label><Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} /></div>
            <div>
              <Label>Trainer</Label>
              <Select value={form.trainer_id || "__none"} onValueChange={v => setForm({ ...form, trainer_id: v === "__none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  {trainers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Color</Label>
              <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-full h-10 rounded border border-border bg-card" />
            </div>
          </div>
          <DialogFooter>
            <button type="submit" data-testid="event-submit" className="w-full bg-primary text-white font-medium py-2.5 rounded-lg hover:bg-primary/90 transition-colors">
              Create event
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BookDialog({ open, onOpenChange, context, members, onBooked }) {
  const [query, setQuery] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const membersById = useMemo(() => {
    const map = {};
    members.forEach(m => { map[m.id] = m; });
    return map;
  }, [members]);

  const reminderText = (m) => {
    if (!context) return "";
    const when = new Date(context.instance_date).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
    return `Hey ${m.name}, quick reminder - your ${context.name} class is at ${context.start_time} on ${when} 💪 See you there!`;
  };

  const messageBooked = (memberId) => {
    const m = membersById[memberId];
    if (!m?.phone) { toast.error("No phone on file"); return; }
    openWhatsApp(m.phone, reminderText(m));
  };

  const messageAllBooked = () => {
    const available = bookings.filter(b => membersById[b.member_id]?.phone);
    if (available.length === 0) { toast.error("No phones on file for booked members"); return; }
    // Only the first window.open is reliable within a single user gesture;
    // subsequent popups get blocked by the browser and our fallback would
    // navigate the tab. So: open the first now, then guide the manager to
    // tap the per-row WhatsApp buttons for the rest.
    const first = available[0];
    const firstMember = membersById[first.member_id];
    openWhatsApp(firstMember.phone, reminderText(firstMember));
    if (available.length > 1) {
      toast.message(`Opened ${firstMember.name} · tap the WhatsApp icon on each row for the other ${available.length - 1}`);
    } else {
      toast.success(`Opened WhatsApp for ${firstMember.name}`);
    }
  };

  const refreshBookings = async () => {
    if (!context) return;
    try {
      const { data } = await endpoints.listClassBookings(context.class_id, context.instance_date);
      setBookings(data);
    } catch {}
  };

  useEffect(() => { if (open && context) refreshBookings(); /* eslint-disable-next-line */ }, [open, context?.id]);

  const filtered = useMemo(() => {
    if (!query.trim()) return members.slice(0, 20);
    const q = query.toLowerCase();
    return members.filter(m =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.phone || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q)
    ).slice(0, 20);
  }, [query, members]);

  const bookedSet = new Set(bookings.map(b => b.member_id));

  const book = async (member) => {
    if (!context || loading) return;
    setLoading(true);
    try {
      const { data: newBooking } = await endpoints.createClassBooking(context.class_id, {
        member_id: member.id, instance_date: context.instance_date,
      });
      // Functional setState using server response - avoids race with reloadBookings()
      setBookings(prev => {
        if (prev.some(b => b.id === newBooking.id)) return prev;
        return [...prev, newBooking];
      });
      toast.success(`${member.name} booked`);
      onBooked();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book");
      // Refresh from server on error so UI re-syncs
      refreshBookings();
    } finally { setLoading(false); }
  };

  const cancel = async (id, name) => {
    if (loading) return;
    setLoading(true);
    try {
      await endpoints.deleteBooking(id);
      setBookings(prev => prev.filter(b => b.id !== id));
      toast.success(`${name} cancelled`);
      onBooked();
    } catch {
      toast.error("Could not cancel");
      refreshBookings();
    } finally { setLoading(false); }
  };

  if (!context) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            Bookings - {context.name}
            <span className="text-xs font-normal text-muted-foreground">
              · {new Date(context.instance_date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {context.start_time}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between mb-3 text-xs">
          <span className="text-muted-foreground">Capacity</span>
          <span className="font-semibold text-foreground" data-testid="book-capacity-count">{bookings.length}/{context.capacity}</span>
        </div>

        {bookings.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Booked</span>
              <button
                type="button"
                onClick={messageAllBooked}
                data-testid="message-all-booked-btn"
                className="inline-flex items-center gap-1 text-[11px] font-medium text-[#25D366] hover:underline"
              >
                <MessageCircle className="w-3 h-3" /> Message all
              </button>
            </div>
            <div className="border border-border rounded-lg divide-y divide-border/60 mb-4" data-testid="booked-list">
              {bookings.map(b => (
                <div key={b.id} className="flex items-center gap-2 p-2 text-sm">
                  <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span className="flex-1 truncate">{b.member_name}</span>
                  <button
                    onClick={() => messageBooked(b.member_id)}
                    data-testid={`message-booked-${b.member_id}`}
                    className="p-1 text-muted-foreground/70 hover:text-[#25D366] rounded"
                    aria-label="Send WhatsApp reminder"
                    title="Send WhatsApp reminder"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => cancel(b.id, b.member_name)} className="p-1 text-muted-foreground/70 hover:text-rose-600 rounded" aria-label="Cancel">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        <Input
          data-testid="book-search-input"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search members to book…"
        />
        <div className="mt-2 max-h-[260px] overflow-auto border border-border rounded-lg divide-y divide-border/60">
          {filtered.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground/70 text-center">No members match.</div>
          ) : filtered.map(m => {
            const already = bookedSet.has(m.id);
            return (
              <div key={m.id} className="flex items-center gap-2 p-2" data-testid={`book-member-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{m.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{m.plan} · {m.phone}</div>
                </div>
                <button
                  disabled={already || loading || bookings.length >= context.capacity}
                  onClick={() => book(m)}
                  className="text-[11px] font-medium bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground px-2.5 py-1 rounded-md"
                >
                  {already ? "Booked" : "Book"}
                </button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Month View ----------
function MonthView({ anchor, data, events, onPickDay, onAddEvent, onEventMoved }) {
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  // Build 6 rows × 7 cols grid for the month containing `anchor`
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startGrid = new Date(firstOfMonth);
  // Mon-first grid: align to Monday
  const dow = (firstOfMonth.getDay() + 6) % 7;
  startGrid.setDate(startGrid.getDate() - dow);
  startGrid.setHours(0, 0, 0, 0);

  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(startGrid);
    d.setDate(d.getDate() + i);
    days.push(d);
  }

  // Group instances + events by ISO date
  const byDate = {};
  (data?.instances || []).forEach(i => {
    (byDate[i.instance_date] = byDate[i.instance_date] || { classes: [], events: [] }).classes.push(i);
  });
  (data?.events || events || []).forEach(ev => {
    const key = (ev.start_at || "").slice(0, 10);
    if (!key) return;
    (byDate[key] = byDate[key] || { classes: [], events: [] }).events.push(ev);
  });

  const todayKey = isoDate(new Date());

  const moveEvent = async (eventId, targetIsoDate) => {
    const allEvents = data?.events || events || [];
    const ev = allEvents.find(e => e.id === eventId);
    if (!ev) return;
    const oldStart = new Date(ev.start_at);
    const oldEnd = new Date(ev.end_at);
    const durationMs = oldEnd - oldStart;
    const [yy, mm, dd] = targetIsoDate.split("-").map(Number);
    const newStart = new Date(yy, mm - 1, dd, oldStart.getHours(), oldStart.getMinutes());
    const newEnd = new Date(newStart.getTime() + durationMs);
    try {
      await endpoints.updateEvent(eventId, {
        start_at: newStart.toISOString(),
        end_at: newEnd.toISOString(),
      });
      toast.success(`Moved "${ev.name}" to ${newStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`);
      onEventMoved && onEventMoved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not move event");
    }
  };

  return (
    <div className="border border-border bg-card rounded-xl overflow-hidden" data-testid="month-view">
      <div className="grid grid-cols-7 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 bg-muted/30">
        {DAY_NAMES.map(n => (
          <div key={n} className="px-2 py-2 text-center border-r border-border/60 last:border-0">{n}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 grid-rows-6">
        {days.map((d, idx) => {
          const key = isoDate(d);
          const inMonth = d.getMonth() === month;
          const isToday = key === todayKey;
          const cell = byDate[key];
          const totalCount = (cell?.classes?.length || 0) + (cell?.events?.length || 0);
          const isDropTarget = dragOver === key;
          return (
            <div
              key={idx}
              data-testid={`month-day-${key}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
              onDragLeave={() => setDragOver(prev => prev === key ? null : prev)}
              onDrop={(e) => {
                e.preventDefault();
                const eid = e.dataTransfer.getData("text/eventId") || dragId;
                setDragOver(null);
                setDragId(null);
                if (eid) moveEvent(eid, key);
              }}
              className={`p-2 min-h-[88px] border-r border-b border-border/60 last:border-r-0 transition-colors ${
                inMonth ? "bg-card" : "bg-muted/10"
              } ${isDropTarget ? "ring-2 ring-primary/60 ring-inset bg-primary/5" : ""}`}
            >
              <div className="flex items-center justify-between mb-1">
                <button
                  type="button"
                  onClick={() => onPickDay(new Date(d))}
                  className={`text-xs font-semibold cursor-pointer ${
                    isToday
                      ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground"
                      : inMonth ? "text-foreground hover:underline" : "text-muted-foreground/50"
                  }`}
                  title="Open day"
                >
                  {d.getDate()}
                </button>
                <div className="flex items-center gap-1">
                  {totalCount > 0 && (
                    <span className="text-[10px] font-semibold text-muted-foreground/80">{totalCount}</span>
                  )}
                  {inMonth && onAddEvent && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onAddEvent(key); }}
                      className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-muted-foreground/70 hover:text-foreground hover:bg-muted transition-opacity"
                      title="Add event on this day"
                      data-testid={`month-add-event-${key}`}
                      style={{ opacity: 1 }}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-0.5 overflow-hidden">
                {(cell?.classes || []).slice(0, 2).map((c, i) => (
                  <div key={`c-${i}`} className="text-[10px] truncate px-1.5 py-0.5 rounded bg-emerald-500/12 text-emerald-700 dark:text-emerald-300">
                    {c.start_time?.slice(0, 5)} {c.name || c.class_name}
                  </div>
                ))}
                {(cell?.events || []).slice(0, 2).map((ev, i) => (
                  <div
                    key={`e-${ev.id || i}`}
                    draggable
                    onDragStart={(e) => {
                      setDragId(ev.id);
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/eventId", ev.id);
                    }}
                    onDragEnd={() => { setDragId(null); setDragOver(null); }}
                    className={`text-[10px] truncate px-1.5 py-0.5 rounded bg-indigo-500/12 text-indigo-700 dark:text-indigo-300 cursor-grab active:cursor-grabbing select-none ${
                      dragId === ev.id ? "opacity-50" : ""
                    }`}
                    title={`Drag to move • ${ev.name}`}
                    data-testid={`month-event-${ev.id}`}
                  >
                    {ev.name}
                  </div>
                ))}
                {totalCount > 4 && (
                  <div className="text-[10px] text-muted-foreground/70">+{totalCount - 4} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-3 py-2 text-[11px] text-muted-foreground/70 bg-muted/20 border-t border-border/60 flex items-center justify-between">
        <span>Drag any event chip to a new day to reschedule.</span>
        <span>Tap the + on a day to add an event.</span>
      </div>
    </div>
  );
}

// ---------- Year View (12 mini-month grid) ----------
function YearView({ anchor, data, events, onPickMonth }) {
  const year = anchor.getFullYear();
  // Build counts per ISO date from instances + events
  const counts = {};
  (data?.instances || []).forEach(i => {
    counts[i.instance_date] = (counts[i.instance_date] || 0) + 1;
  });
  (events || []).forEach(ev => {
    const key = (ev.start_at || "").slice(0, 10);
    if (key && key.startsWith(String(year))) counts[key] = (counts[key] || 0) + 1;
  });
  const todayKey = isoDate(new Date());

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="year-view">
      {Array.from({ length: 12 }).map((_, m) => {
        const monthDate = new Date(year, m, 1);
        const lastDay = new Date(year, m + 1, 0).getDate();
        const firstDow = (monthDate.getDay() + 6) % 7; // Mon=0
        const cells = [];
        for (let i = 0; i < firstDow; i++) cells.push(null);
        for (let d = 1; d <= lastDay; d++) cells.push(d);
        while (cells.length % 7 !== 0) cells.push(null);

        const total = Array.from({ length: lastDay }).reduce((sum, _v, idx) => {
          const k = `${year}-${String(m + 1).padStart(2, "0")}-${String(idx + 1).padStart(2, "0")}`;
          return sum + (counts[k] || 0);
        }, 0);

        return (
          <button
            key={m}
            type="button"
            onClick={() => onPickMonth(new Date(year, m, 1))}
            data-testid={`year-month-${m}`}
            className="text-left border border-border rounded-xl p-3 bg-card hover:border-foreground/25 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-display text-sm font-semibold text-foreground">
                {monthDate.toLocaleDateString(undefined, { month: "long" })}
              </span>
              <span className="text-[10px] text-muted-foreground/70">{total} entr{total === 1 ? "y" : "ies"}</span>
            </div>
            <div className="grid grid-cols-7 text-[9px] font-semibold uppercase text-muted-foreground/60 mb-1">
              {DAY_NAMES.map(n => <div key={n} className="text-center">{n[0]}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-px">
              {cells.map((d, idx) => {
                if (!d) return <div key={idx} className="h-6" />;
                const k = `${year}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                const c = counts[k] || 0;
                const isToday = k === todayKey;
                let bg = "bg-transparent";
                if (c > 0) bg = "bg-primary/15";
                if (c >= 3) bg = "bg-primary/30";
                if (c >= 5) bg = "bg-primary/55";
                return (
                  <div
                    key={idx}
                    title={c > 0 ? `${k}: ${c}` : k}
                    className={`h-6 flex items-center justify-center text-[10px] rounded-sm ${bg} ${
                      isToday ? "ring-1 ring-primary text-primary font-bold" : "text-foreground/80"
                    }`}
                  >
                    {d}
                  </div>
                );
              })}
            </div>
          </button>
        );
      })}
    </div>
  );
}

